import React from 'react';
import { Helmet } from 'react-helmet-async';

// Comprehensive SEO Keywords for Codewave Technologies Limited
const DEFAULT_KEYWORDS = [
  // Primary SEO Keywords
  'Codewave',
  'Codewave Technologies Limited',
  'ICT company in Kenya',
  'telecom solutions Kenya',
  'bulk SMS Kenya',
  'USSD service provider Kenya',
  'SMS gateway Kenya',
  'mobile payment solutions Kenya',
  'business communication solutions',
  'API integration services Kenya',
  // Secondary SEO Keywords
  'bulk SMS service provider',
  'USSD application development',
  'short code services Kenya',
  'IVR solutions Kenya',
  'voice SMS services',
  'enterprise messaging solutions',
  'mobile API services',
  'business SMS marketing',
  'customer engagement platforms',
  'digital communication solutions',
  // Long-Tail SEO Keywords
  'affordable bulk SMS services in Kenya',
  'best USSD service provider in Kenya',
  'SMS gateway for businesses in Kenya',
  'API integration for mobile payments',
  'IVR and voice solutions for companies',
  'short code and USSD setup services',
  'telecom API provider in Kenya',
  'business messaging solutions Kenya',
  // Local SEO Keywords
  'ICT services Nairobi',
  'telecom company Nairobi Kenya',
  'bulk SMS Nairobi',
  'USSD services Nairobi',
  'Kenyan ICT solutions provider'
].join(', ');

const SEOHead = ({
  title = 'Codewave Technologies Limited - Premier ICT & Telecom Solutions in Kenya',
  description = 'Codewave Technologies Limited is a leading ICT company in Kenya offering telecom solutions, bulk SMS services, USSD service provider, SMS gateway, mobile payment solutions, and API integration services. Best USSD service provider in Kenya.',
  keywords = DEFAULT_KEYWORDS,
  image = 'https://www.codewave.co.ke/og-image.jpg',
  url = 'https://www.codewave.co.ke',
  type = 'website',
  author = 'Codewave Technologies Limited',
  siteName = 'Codewave Technologies Limited',
  twitterHandle = '@codewave',
  additionalMetaTags = [],
  structuredData = null,
  canonical = null
}) => {
  const currentUrl = canonical || url;
  const fullTitle = title.includes('Codewave') ? title : `${title} | Codewave Technologies Limited`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_KE" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />
      
      {/* Additional Meta Tags */}
      {additionalMetaTags.map((tag, index) => (
        <meta key={index} {...tag} />
      ))}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Theme Color */}
      <meta name="theme-color" content="#105090" />
      <meta name="msapplication-TileColor" content="#105090" />
      
      {/* Language */}
      <meta httpEquiv="content-language" content="en" />
      
      {/* Local SEO Geographic Tags */}
      <meta name="geo.region" content="KE-110" />
      <meta name="geo.country" content="Kenya" />
      <meta name="geo.placename" content="Nairobi" />
      <meta name="geo.position" content="-1.2921;36.8219" />
      <meta name="ICBM" content="-1.2921, 36.8219" />
      
      {/* Business Tags */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
    </Helmet>
  );
};

export default SEOHead;